import { Injectable } from '@angular/core';
import { SmartTableService, ColumnSortedEvent, ValueTransformer } from './smart-table.service';
import { getValueByPath } from '../util/helpers';

export interface LocalColumnRegistration {
	label: string;
	field: string | string[];
	valueTransformer?: ValueTransformer;
	options?: {
		hideable?: boolean;
		hide?: boolean;
		stackable?: boolean;
		searchable?: boolean;
	};
	isImage?: boolean;
	headerStyle?: Record<string, string>;
	cellStyle?: Record<string, string>;
}

@Injectable()
export class LocalDataService<T = any> extends SmartTableService {
	private _allData: T[] = [];
	private _filteredSorted: T[] = [];
	private _pageContent: T[] = [];

	constructor() {
		super();
	}

	public registerFields(fields: Record<string, LocalColumnRegistration>): void {
		this.flushColumns();
		for (const key in fields) {
			const column = fields[key];
			this.registerColumn({
				...column,
				...column.options,
				label: column.label || key,
			});
		}
	}

	public setData(data: T[]): void {
		this._allData = data || [];
		this.refresh();
	}

	public get entities(): T[] {
		return this._pageContent;
	}

	public get totalFiltered(): number {
		return this._filteredSorted.length;
	}

	public refresh(): void {
		const criteria = this.getCriteriaAsFilters();

		// Filter
		const filters = criteria.filter((c) => !!c.filter && !!c.value);
		let filtered = this._allData;

		if (filters.length > 0) {
			filtered = this._allData.filter((item) => {
				return filters.every((criterion) => {
					const field = criterion.filter!;
					const searchValue = criterion.value!.toLowerCase();

					const col = this.columns.find((c) => {
						if (Array.isArray(c.field)) {
							return c.field.includes(field) || c.field[0] === field;
						}
						return c.field === field;
					});

					const fields = col && Array.isArray(col.field) ? col.field : [field];

					return fields.some((f) => {
						const val = getValueByPath(item as any, f);
						if (val === undefined || val === null) {
							return false;
						}
						return String(val).toLowerCase().includes(searchValue);
					});
				});
			});
		}

		// Sort
		const sortCriteria: ColumnSortedEvent = {
			sortCols: criteria.filter((c) => !!c.order && !!c.value).map((c) => ({ id: c.order!, dir: c.value })),
		};

		if (sortCriteria.sortCols.length > 0) {
			this._filteredSorted = this.sortByCriteria(sortCriteria, [...filtered]);
		} else {
			this._filteredSorted = [...filtered];
		}

		// Paginate
		const page = this.currentPage || 0;
		const size = this.pageSize || 10;
		const totalPages = Math.max(1, Math.ceil(this._filteredSorted.length / size));

		const effectivePage = Math.min(page, totalPages - 1);
		const start = effectivePage * size;
		this._pageContent = this._filteredSorted.slice(start, start + size);

		this.config({
			currentPage: effectivePage,
			totalPages,
			datasetSize: this._filteredSorted.length,
			pageSize: size,
		});
	}
}
